import { Spacing } from '@persona-id/design-tokens';
import { EyeIcon, EyeOffIcon } from '@persona-id/design-tokens/icons';
import { ChangeEventHandler, createRef } from 'react';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import Tooltip from 'dashboard/components/Tooltip';
import Button from 'design-system/components/Button';
import { paragraphMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';

const FieldContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: 0 ${Spacing[2]} 0 ${Spacing[4]};
  height: ${({ theme }) => theme.element.height.md};
`;

const DisplayInputRegular = styled.input`
  ${paragraphMixins.md};
  font-family: ${({ theme }) => theme.text.fontFamily.base};
  width: 100%;
  color: ${({ theme }) => theme.color.text.primary};
`;

const DisplayInputMono = styled.input`
  ${paragraphMixins.md};
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  width: 100%;
  color: ${({ theme }) => theme.color.text.primary};
`;

const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

const TooltipButton = styled(Button)`
  margin-left: ${Spacing[1]};
`;

interface CopyableFieldProps {
  className?: string;
  id?: string;
  value: string;
  label?: string;
  type?: 'text' | 'password';
  'data-test'?: string;
  editable?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

const CopyableField = ({
  className,
  id,
  value,
  label,
  editable = false,
  onChange = () => {},
  type = 'text',
  'data-test': dataTest = 'copyable-field',
}: CopyableFieldProps) => {
  const [masked, { toggle: toggleMask }] = useToggle(type === 'password');
  const valueInput = createRef<HTMLInputElement>();

  const copyKey = () => {
    // Copies from the hidden input so fields of type='password' can be copied
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    valueInput.current.select();
    document.execCommand('copy');
  };

  const DisplayInput = type === 'password' ? DisplayInputMono : DisplayInputRegular;

  return (
    <div className={className}>
      {label != null && <FormControlLabel>{label}</FormControlLabel>}
      <FieldContainer>
        <HiddenInput value={value} readOnly={!editable} ref={valueInput} tabIndex={-1} />
        <DisplayInput
          value={value}
          onChange={onChange}
          type={masked ? 'password' : 'text'}
          id={id}
          data-test={dataTest}
          tabIndex={-1}
          readOnly={!editable}
        />

        {type === 'password' && (
          <Button
            type='button'
            width='icon'
            variant='inverse'
            id='copyable-field__toggle-mask-button'
            onClick={toggleMask}
            data-test={`${dataTest}__toggle-password-type`}
          >
            {masked ? <EyeOffIcon /> : <EyeIcon />}
          </Button>
        )}

        <Tooltip placement='top' trigger='click' tooltip='Copied!' hideArrow={false}>
          <TooltipButton
            type='button'
            variant='basic'
            id='copyable-field__copy-button'
            onClick={copyKey}
            data-test={`${dataTest}__copy-to-clipboard`}
          >
            Copy
          </TooltipButton>
        </Tooltip>
      </FieldContainer>
    </div>
  );
};

export default CopyableField;
